import { useSyncExternalStore } from 'react'

/**
 * Display timezone for the current session.
 *
 * Every stored timestamp is UTC; this only decides how the UI renders them.
 * Resolution order: the user's own timezone, then the tenant's default, then
 * the browser's. `setTimezoneSources` is fed from the auth store by
 * `stores/timezoneSync.ts`; nothing else should write it.
 */

export function isValidTimeZone(tz: string | null | undefined): tz is string {
  if (!tz) return false
  try {
    new Intl.DateTimeFormat(undefined, { timeZone: tz })
    return true
  } catch {
    return false
  }
}

export function detectBrowserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
  } catch {
    return 'UTC'
  }
}

export interface TimezoneSources {
  user?: string | null
  tenant?: string | null
}

export type TimezoneSource = 'user' | 'tenant' | 'browser'

/** Pure resolver, exported for tests and for settings UIs that show "inherited from…". */
export function resolveTimezone(sources: TimezoneSources): { timezone: string; source: TimezoneSource } {
  if (isValidTimeZone(sources.user)) return { timezone: sources.user, source: 'user' }
  if (isValidTimeZone(sources.tenant)) return { timezone: sources.tenant, source: 'tenant' }
  return { timezone: detectBrowserTimezone(), source: 'browser' }
}

let current = resolveTimezone({})
const listeners = new Set<() => void>()

export function setTimezoneSources(sources: TimezoneSources): void {
  const next = resolveTimezone(sources)
  if (next.timezone === current.timezone && next.source === current.source) return
  current = next
  listeners.forEach(l => l())
}

/** The IANA timezone every timestamp should currently render in. */
export function getActiveTimezone(): string {
  return current.timezone
}

export function getActiveTimezoneSource(): TimezoneSource {
  return current.source
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/**
 * Subscribe a component to timezone changes so it re-renders (and re-formats)
 * when the user edits their timezone in settings. Components that merely call
 * `formatTimestamp` during render do not need this unless they are memoised.
 */
export function useActiveTimezone(): string {
  return useSyncExternalStore(subscribe, getActiveTimezone, getActiveTimezone)
}

/** Short zone label for the active timezone, e.g. "IST", "EST", "GMT+5:30". */
export function getTimezoneAbbr(date: Date = new Date(), timeZone: string = getActiveTimezone()): string {
  try {
    const parts = new Intl.DateTimeFormat(undefined, { timeZone, timeZoneName: 'short' }).formatToParts(date)
    return parts.find(p => p.type === 'timeZoneName')?.value ?? ''
  } catch {
    return ''
  }
}

/** All selectable IANA zones; falls back to a short list on runtimes without supportedValuesOf. */
export function listTimezones(): string[] {
  const fallback = [
    'UTC', 'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
    'America/Sao_Paulo', 'Europe/London', 'Europe/Berlin', 'Africa/Johannesburg',
    'Asia/Dubai', 'Asia/Kolkata', 'Asia/Singapore', 'Asia/Tokyo', 'Australia/Sydney',
  ]
  const intl = Intl as typeof Intl & { supportedValuesOf?: (key: 'timeZone') => string[] }
  let zones: string[]
  try {
    zones = intl.supportedValuesOf ? intl.supportedValuesOf('timeZone') : fallback
  } catch {
    zones = fallback
  }
  // supportedValuesOf omits "UTC" on some engines; every picker needs it.
  return zones.includes('UTC') ? zones : ['UTC', ...zones]
}

/**
 * Install the active timezone as the default `timeZone` for
 * Date#toLocaleString / toLocaleDateString / toLocaleTimeString.
 *
 * This is how the ~220 legacy call sites (and chart/table libraries) honour the
 * user's timezone without being rewritten one by one. An explicit `timeZone` in
 * the caller's options always wins, so code that deliberately formats in UTC or
 * a schedule's own zone is untouched. Number#toLocaleString is a different
 * prototype and is not affected.
 *
 * Call once at app start; calling again is a no-op. New code should prefer the
 * helpers in `formatTimestamp.ts`, which pass the zone explicitly.
 */
let patched = false
export function installDateTimezoneDefaults(): void {
  if (patched) return
  patched = true
  const methods = ['toLocaleString', 'toLocaleDateString', 'toLocaleTimeString'] as const
  for (const method of methods) {
    const original = Date.prototype[method]
    Date.prototype[method] = function (
      this: Date,
      locales?: Intl.LocalesArgument,
      options?: Intl.DateTimeFormatOptions,
    ): string {
      if (options?.timeZone) return original.call(this, locales, options)
      return original.call(this, locales, { ...options, timeZone: getActiveTimezone() })
    }
  }
}
